import { useEffect, useState } from "react";
import { api, assetUrl } from "../lib/api";
import { useBrand } from "../context/BrandContext";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Film, Play, Eye, Clock, Timer } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const fmtSec = (ms) => (ms == null ? "—" : `${(ms / 1000).toFixed(1)}s`);
const fmtTotal = (ms) => {
  if (ms == null) return "—";
  const s = ms / 1000;
  return s >= 60 ? `${(s / 60).toFixed(1)} min` : `${s.toFixed(0)}s`;
};

export const ReelInsights = () => {
  const { selected } = useBrand();
  const [reels, setReels] = useState(null);

  useEffect(() => {
    setReels(null);
    api.get("/analytics/reels", { params: selected ? { brand: selected } : {} }).then((r) => setReels(r.data)).catch(() => setReels([]));
  }, [selected]);

  return (
    <Card data-testid="reel-insights-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2"><Film size={18} /> Reel insights</CardTitle>
      </CardHeader>
      <CardContent>
        {!reels ? (
          <p className="text-sm text-muted-foreground">Loading watch-time data…</p>
        ) : reels.length === 0 ? (
          <p className="text-sm text-muted-foreground">No published Reels yet — create a video on any post and publish it to see watch-time and retention here.</p>
        ) : (
          <div className="space-y-5">
            {reels.map((r) => (
              <div key={r.id} className="flex gap-4 items-start border-b border-border pb-5 last:border-0 last:pb-0" data-testid={`reel-insight-row-${r.id}`}>
                {r.image_url && <img src={assetUrl(r.image_url)} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold truncate">{r.title}</p>
                    <Badge variant="secondary" className="capitalize text-[10px]">{r.platform}</Badge>
                    {r.brand && <span className="text-xs text-muted-foreground">{r.brand}</span>}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground mt-2 flex-wrap">
                    <span className="flex items-center gap-1" data-testid={`reel-plays-${r.id}`}><Play size={12} /> {r.insights.plays ?? "—"} plays</span>
                    <span className="flex items-center gap-1"><Eye size={12} /> {r.insights.reach ?? "—"} reach</span>
                    <span className="flex items-center gap-1" data-testid={`reel-avg-watch-${r.id}`}><Clock size={12} /> {fmtSec(r.insights.avg_watch_ms)} avg watch</span>
                    <span className="flex items-center gap-1"><Timer size={12} /> {fmtTotal(r.insights.total_watch_ms)} total</span>
                  </div>
                  {r.insights.retention?.length > 1 && (
                    <div className="mt-3 h-24" data-testid={`reel-retention-${r.id}`}>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Viewer retention — % still watching</p>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={r.insights.retention} margin={{ top: 2, right: 4, left: -26, bottom: 0 }}>
                          <XAxis dataKey="t" tick={{ fontSize: 9 }} tickFormatter={(t) => `${t}%`} interval={4} />
                          <YAxis tick={{ fontSize: 9 }} domain={[0, 100]} />
                          <Tooltip formatter={(v) => [`${v}%`, "watching"]} labelFormatter={(t) => `${t}% through video`} />
                          <Area type="monotone" dataKey="v" stroke="hsl(var(--accent))" fill="hsl(var(--accent) / 0.25)" strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
